import axiosInstance from '../../../config/axiosInstance';
import type {
  IndustrialWorkflowStageAutomation,
  IndustrialWorkflowStageAutomationUpsertPayload,
  IndustrialWorkflowStageTaskTemplate,
  IndustrialWorkflowStageTaskTemplateUpsertPayload,
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowCase,
  IndustrialWorkflowCaseCompleteStagePayload,
  IndustrialWorkflowCaseCreatePayload,
  IndustrialWorkflowCaseEvent,
  IndustrialWorkflowCaseCommentCreatePayload,
  IndustrialWorkflowExternalFollowUpCreatePayload,
  IndustrialWorkflowCaseStatus,
  IndustrialWorkflowCaseStageSchedule,
  IndustrialWorkflowCaseStageScheduleOverrideApplyPayload,
  IndustrialWorkflowCaseStageScheduleOverridePreview,
  IndustrialWorkflowCaseStageScheduleOverridePreviewPayload,
  IndustrialWorkflowCaseStageScheduleOverrideResult,
  IndustrialWorkflowCaseStageScheduleRecalculationResult,
  IndustrialWorkflowCaseUpdatePayload,
  IndustrialWorkflowFieldDefinition,
  IndustrialWorkflowFieldDefinitionUpsertPayload,
  IndustrialWorkflowWorkReport,
  IndustrialWorkflowWorkReportCancelPayload,
  IndustrialWorkflowWorkReportCompletePayload,
  IndustrialWorkflowWorkReportCreatePayload,
  IndustrialWorkflowWorkReportDefinition,
  IndustrialWorkflowWorkReportDefinitionUpsertPayload,
  IndustrialWorkflowWorkReportFieldDefinition,
  IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload,
  IndustrialWorkflowWorkReportUpdatePayload,
  IndustrialWorkflowOperationsDashboard,
  IndustrialWorkflowOperatorMyWork,
  IndustrialWorkflowProcessDefinition,
  IndustrialWorkflowProcessDefinitionUpsertPayload,
  IndustrialWorkflowStageDefinition,
  IndustrialWorkflowStageDefinitionUpsertPayload,
  IndustrialWorkflowStageDeadlineAdjustPayload,
  IndustrialWorkflowStageDeadlineAdjustmentResult,
  IndustrialWorkflowStageDeadlinePreview,
  IndustrialWorkflowStageDeadlinePreviewPayload,
  IndustrialWorkflowTask,
  IndustrialWorkflowTaskDeadlineAdjustPayload,
  IndustrialWorkflowTaskDeadlineAdjustmentResult,
  IndustrialWorkflowTaskDeadlinePreview,
  IndustrialWorkflowTaskDeadlinePreviewPayload,
  IndustrialWorkflowTaskCreatePayload,
  IndustrialWorkflowTaskUpdatePayload,
  IndustrialWorkflowTransitionDefinition,
  IndustrialWorkflowTransitionDefinitionUpsertPayload,
  IndustrialWorkflowWorkArea,
  IndustrialWorkflowWorkAreaMember,
  IndustrialWorkflowWorkAreaMembersReplacePayload,
  IndustrialWorkflowWorkAreaUpsertPayload,
  IndustrialWorkflowsContext,
} from '../types/industrialWorkflows';

export async function getIndustrialWorkflowsContext() {
  const response = await axiosInstance.get<IndustrialWorkflowsContext>(
    '/industrial-workflows/context',
  );

  return response.data;
}

export async function listIndustrialWorkflowAssignableUsers() {
  const response = await axiosInstance.get<IndustrialWorkflowAssignableUser[]>(
    '/industrial-workflows/users/assignable',
  );

  return response.data;
}

export async function listIndustrialWorkflowWorkAreas(
  includeInactive = false,
) {
  const response = await axiosInstance.get<IndustrialWorkflowWorkArea[]>(
    '/industrial-workflows/work-areas',
    {
      params: {
        include_inactive: includeInactive,
      },
    },
  );

  return response.data;
}

export async function createIndustrialWorkflowWorkArea(
  payload: IndustrialWorkflowWorkAreaUpsertPayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowWorkArea>(
    '/industrial-workflows/work-areas',
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowWorkArea(
  workAreaId: string,
  payload: IndustrialWorkflowWorkAreaUpsertPayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowWorkArea>(
    `/industrial-workflows/work-areas/${workAreaId}`,
    payload,
  );

  return response.data;
}

export async function deleteIndustrialWorkflowWorkArea(
  workAreaId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/work-areas/${workAreaId}`,
  );
}

export async function listIndustrialWorkflowWorkAreaMembers(
  workAreaId: string,
) {
  const response = await axiosInstance.get<IndustrialWorkflowWorkAreaMember[]>(
    `/industrial-workflows/work-areas/${workAreaId}/members`,
  );

  return response.data;
}

export async function listIndustrialWorkflowMyWorkAreaMemberships() {
  const response = await axiosInstance.get<IndustrialWorkflowWorkAreaMember[]>(
    '/industrial-workflows/work-areas/my-memberships',
  );

  return response.data;
}

export async function replaceIndustrialWorkflowWorkAreaMembers(
  workAreaId: string,
  payload: IndustrialWorkflowWorkAreaMembersReplacePayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowWorkAreaMember[]>(
    `/industrial-workflows/work-areas/${workAreaId}/members`,
    payload,
  );

  return response.data;
}

export async function listIndustrialWorkflowProcesses(
  includeInactive = false,
) {
  const response = await axiosInstance.get<IndustrialWorkflowProcessDefinition[]>(
    '/industrial-workflows/processes',
    {
      params: {
        include_inactive: includeInactive,
      },
    },
  );

  return response.data;
}

export async function createIndustrialWorkflowProcess(
  payload: IndustrialWorkflowProcessDefinitionUpsertPayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowProcessDefinition>(
    '/industrial-workflows/processes',
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowProcess(
  processDefinitionId: string,
  payload: IndustrialWorkflowProcessDefinitionUpsertPayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowProcessDefinition>(
    `/industrial-workflows/processes/${processDefinitionId}`,
    payload,
  );

  return response.data;
}

export async function deleteIndustrialWorkflowProcess(
  processDefinitionId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}`,
  );
}

export async function listIndustrialWorkflowStages(
  processDefinitionId: string,
  includeInactive = false,
) {
  const response = await axiosInstance.get<IndustrialWorkflowStageDefinition[]>(
    `/industrial-workflows/processes/${processDefinitionId}/stages`,
    {
      params: {
        include_inactive: includeInactive,
      },
    },
  );

  return response.data;
}

export async function createIndustrialWorkflowStage(
  processDefinitionId: string,
  payload: IndustrialWorkflowStageDefinitionUpsertPayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowStageDefinition>(
    `/industrial-workflows/processes/${processDefinitionId}/stages`,
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowStage(
  processDefinitionId: string,
  stageDefinitionId: string,
  payload: IndustrialWorkflowStageDefinitionUpsertPayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowStageDefinition>(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}`,
    payload,
  );

  return response.data;
}

export async function deleteIndustrialWorkflowStage(
  processDefinitionId: string,
  stageDefinitionId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}`,
  );
}

export async function listIndustrialWorkflowStageTransitions(
  processDefinitionId: string,
  stageDefinitionId: string,
  includeInactive = false,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowTransitionDefinition[]>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/transitions`,
      {
        params: {
          include_inactive: includeInactive,
        },
      },
    );

  return response.data;
}

export async function createIndustrialWorkflowStageTransition(
  processDefinitionId: string,
  stageDefinitionId: string,
  payload: IndustrialWorkflowTransitionDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowTransitionDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/transitions`,
      payload,
    );

  return response.data;
}

export async function updateIndustrialWorkflowStageTransition(
  processDefinitionId: string,
  stageDefinitionId: string,
  transitionDefinitionId: string,
  payload: IndustrialWorkflowTransitionDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.put<IndustrialWorkflowTransitionDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/transitions/${transitionDefinitionId}`,
      payload,
    );

  return response.data;
}

export async function deleteIndustrialWorkflowStageTransition(
  processDefinitionId: string,
  stageDefinitionId: string,
  transitionDefinitionId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/transitions/${transitionDefinitionId}`,
  );
}

export async function listIndustrialWorkflowStageTaskTemplates(
  processDefinitionId: string,
  stageDefinitionId: string,
  includeInactive = false,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowStageTaskTemplate[]>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/task-templates`,
      {
        params: {
          include_inactive: includeInactive,
        },
      },
    );

  return response.data;
}

export async function createIndustrialWorkflowStageTaskTemplate(
  processDefinitionId: string,
  stageDefinitionId: string,
  payload: IndustrialWorkflowStageTaskTemplateUpsertPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowStageTaskTemplate>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/task-templates`,
      payload,
    );

  return response.data;
}

export async function updateIndustrialWorkflowStageTaskTemplate(
  processDefinitionId: string,
  stageDefinitionId: string,
  taskTemplateId: string,
  payload: IndustrialWorkflowStageTaskTemplateUpsertPayload,
) {
  const response =
    await axiosInstance.put<IndustrialWorkflowStageTaskTemplate>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/task-templates/${taskTemplateId}`,
      payload,
    );

  return response.data;
}

export async function deleteIndustrialWorkflowStageTaskTemplate(
  processDefinitionId: string,
  stageDefinitionId: string,
  taskTemplateId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/task-templates/${taskTemplateId}`,
  );
}

export async function getIndustrialWorkflowStageAutomation(
  processDefinitionId: string,
  stageDefinitionId: string,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowStageAutomation>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/automation`,
    );

  return response.data;
}

export async function updateIndustrialWorkflowStageAutomation(
  processDefinitionId: string,
  stageDefinitionId: string,
  payload: IndustrialWorkflowStageAutomationUpsertPayload,
) {
  const response =
    await axiosInstance.put<IndustrialWorkflowStageAutomation>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/automation`,
      payload,
    );

  return response.data;
}

export async function listIndustrialWorkflowFields(
  processDefinitionId: string,
  includeInactive = false,
) {
  const response = await axiosInstance.get<IndustrialWorkflowFieldDefinition[]>(
    `/industrial-workflows/processes/${processDefinitionId}/fields`,
    {
      params: {
        include_inactive: includeInactive,
      },
    },
  );

  return response.data;
}

export async function createIndustrialWorkflowField(
  processDefinitionId: string,
  payload: IndustrialWorkflowFieldDefinitionUpsertPayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowFieldDefinition>(
    `/industrial-workflows/processes/${processDefinitionId}/fields`,
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowField(
  processDefinitionId: string,
  fieldDefinitionId: string,
  payload: IndustrialWorkflowFieldDefinitionUpsertPayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowFieldDefinition>(
    `/industrial-workflows/processes/${processDefinitionId}/fields/${fieldDefinitionId}`,
    payload,
  );

  return response.data;
}

export async function listIndustrialWorkflowWorkReportDefinitions(
  processDefinitionId: string,
  stageDefinitionId: string,
  includeInactive = false,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowWorkReportDefinition[]>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions`,
      {
        params: {
          include_inactive: includeInactive,
        },
      },
    );

  return response.data;
}

export async function createIndustrialWorkflowWorkReportDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  payload: IndustrialWorkflowWorkReportDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowWorkReportDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions`,
      payload,
    );

  return response.data;
}

export async function updateIndustrialWorkflowWorkReportDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
  payload: IndustrialWorkflowWorkReportDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.put<IndustrialWorkflowWorkReportDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}`,
      payload,
    );

  return response.data;
}

export async function deleteIndustrialWorkflowWorkReportDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}`,
  );
}

export async function listIndustrialWorkflowWorkReportFieldDefinitions(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
  includeInactive = false,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowWorkReportFieldDefinition[]>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}/fields`,
      {
        params: {
          include_inactive: includeInactive,
        },
      },
    );

  return response.data;
}

export async function createIndustrialWorkflowWorkReportFieldDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
  payload: IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowWorkReportFieldDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}/fields`,
      payload,
    );

  return response.data;
}

export async function updateIndustrialWorkflowWorkReportFieldDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
  fieldDefinitionId: string,
  payload: IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload,
) {
  const response =
    await axiosInstance.put<IndustrialWorkflowWorkReportFieldDefinition>(
      `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}/fields/${fieldDefinitionId}`,
      payload,
    );

  return response.data;
}

export async function deleteIndustrialWorkflowWorkReportFieldDefinition(
  processDefinitionId: string,
  stageDefinitionId: string,
  workReportDefinitionId: string,
  fieldDefinitionId: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/processes/${processDefinitionId}/stages/${stageDefinitionId}/work-report-definitions/${workReportDefinitionId}/fields/${fieldDefinitionId}`,
  );
}

export async function listIndustrialWorkflowWorkReports(
  caseId: string,
  currentStageOnly = false,
) {
  const response = await axiosInstance.get<IndustrialWorkflowWorkReport[]>(
    `/industrial-workflows/cases/${caseId}/work-reports`,
    {
      params: {
        current_stage_only: currentStageOnly,
      },
    },
  );

  return response.data;
}

export async function createIndustrialWorkflowWorkReport(
  caseId: string,
  payload: IndustrialWorkflowWorkReportCreatePayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowWorkReport>(
    `/industrial-workflows/cases/${caseId}/work-reports`,
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowWorkReport(
  caseId: string,
  workReportId: string,
  payload: IndustrialWorkflowWorkReportUpdatePayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowWorkReport>(
    `/industrial-workflows/cases/${caseId}/work-reports/${workReportId}`,
    payload,
  );

  return response.data;
}

export async function completeIndustrialWorkflowWorkReport(
  caseId: string,
  workReportId: string,
  payload: IndustrialWorkflowWorkReportCompletePayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowWorkReport>(
    `/industrial-workflows/cases/${caseId}/work-reports/${workReportId}/complete`,
    payload,
  );

  return response.data;
}

export async function cancelIndustrialWorkflowWorkReport(
  caseId: string,
  workReportId: string,
  payload: IndustrialWorkflowWorkReportCancelPayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowWorkReport>(
    `/industrial-workflows/cases/${caseId}/work-reports/${workReportId}/cancel`,
    payload,
  );

  return response.data;
}

export async function listIndustrialWorkflowCases(params: {
  process_definition_id: string;
  stage_definition_id?: string;
  case_status?: IndustrialWorkflowCaseStatus;
  assigned_user_id?: string;
}) {
  const response = await axiosInstance.get<IndustrialWorkflowCase[]>(
    '/industrial-workflows/cases',
    {
      params,
    },
  );

  return response.data;
}

export async function listIndustrialWorkflowCaseEvents(
  caseId: string,
) {
  const response = await axiosInstance.get<IndustrialWorkflowCaseEvent[]>(
    `/industrial-workflows/cases/${caseId}/events`,
  );

  return response.data;
}

export async function createIndustrialWorkflowCaseComment(
  caseId: string,
  payload: IndustrialWorkflowCaseCommentCreatePayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowCaseEvent>(
      `/industrial-workflows/cases/${caseId}/comments`,
      payload,
    );

  return response.data;
}

export async function createIndustrialWorkflowExternalFollowUp(
  caseId: string,
  payload: IndustrialWorkflowExternalFollowUpCreatePayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowCaseEvent>(
      `/industrial-workflows/cases/${caseId}/external-followups`,
      payload,
    );

  return response.data;
}

export async function previewIndustrialWorkflowStageDeadline(
  caseId: string,
  payload:
    IndustrialWorkflowStageDeadlinePreviewPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowStageDeadlinePreview>(
      `/industrial-workflows/cases/${caseId}/stage-deadline/preview`,
      payload,
    );

  return response.data;
}

export async function adjustIndustrialWorkflowStageDeadline(
  caseId: string,
  payload:
    IndustrialWorkflowStageDeadlineAdjustPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowStageDeadlineAdjustmentResult>(
      `/industrial-workflows/cases/${caseId}/stage-deadline/adjust`,
      payload,
    );

  return response.data;
}

export async function previewIndustrialWorkflowTaskDeadline(
  caseId: string,
  taskId: string,
  payload:
    IndustrialWorkflowTaskDeadlinePreviewPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowTaskDeadlinePreview>(
      `/industrial-workflows/cases/${caseId}/tasks/${taskId}/deadline/preview`,
      payload,
    );

  return response.data;
}

export async function adjustIndustrialWorkflowTaskDeadline(
  caseId: string,
  taskId: string,
  payload:
    IndustrialWorkflowTaskDeadlineAdjustPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowTaskDeadlineAdjustmentResult>(
      `/industrial-workflows/cases/${caseId}/tasks/${taskId}/deadline/adjust`,
      payload,
    );

  return response.data;
}

export async function listIndustrialWorkflowTasks(
  caseId: string,
) {
  const response = await axiosInstance.get<IndustrialWorkflowTask[]>(
    `/industrial-workflows/cases/${caseId}/tasks`,
  );

  return response.data;
}

export async function createIndustrialWorkflowTaskFromSuggestedTemplate(
  caseId: string,
  taskTemplateId: string,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowTask>(
      `/industrial-workflows/cases/${caseId}/suggested-task-templates/${taskTemplateId}/instantiate`,
    );

  return response.data;
}

export async function createIndustrialWorkflowTask(
  caseId: string,
  payload: IndustrialWorkflowTaskCreatePayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowTask>(
    `/industrial-workflows/cases/${caseId}/tasks`,
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowTask(
  caseId: string,
  taskId: string,
  payload: IndustrialWorkflowTaskUpdatePayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowTask>(
    `/industrial-workflows/cases/${caseId}/tasks/${taskId}`,
    payload,
  );

  return response.data;
}

export async function claimIndustrialWorkflowTask(
  caseId: string,
  taskId: string,
) {
  const response = await axiosInstance.post<IndustrialWorkflowTask>(
    `/industrial-workflows/cases/${caseId}/tasks/${taskId}/claim`,
  );

  return response.data;
}

export async function startIndustrialWorkflowTask(
  caseId: string,
  taskId: string,
) {
  const response = await axiosInstance.post<IndustrialWorkflowTask>(
    `/industrial-workflows/cases/${caseId}/tasks/${taskId}/start`,
  );

  return response.data;
}

export async function completeIndustrialWorkflowTask(
  caseId: string,
  taskId: string,
) {
  const response = await axiosInstance.post<IndustrialWorkflowTask>(
    `/industrial-workflows/cases/${caseId}/tasks/${taskId}/complete`,
  );

  return response.data;
}

export async function createIndustrialWorkflowCase(
  payload: IndustrialWorkflowCaseCreatePayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowCase>(
    '/industrial-workflows/cases',
    payload,
  );

  return response.data;
}

export async function updateIndustrialWorkflowCase(
  caseId: string,
  payload: IndustrialWorkflowCaseUpdatePayload,
) {
  const response = await axiosInstance.put<IndustrialWorkflowCase>(
    `/industrial-workflows/cases/${caseId}`,
    payload,
  );

  return response.data;
}

export async function deleteIndustrialWorkflowCase(
  caseId: string,
  referenceCode: string,
) {
  await axiosInstance.delete(
    `/industrial-workflows/cases/${caseId}`,
    {
      params: {
        reference_code: referenceCode,
      },
    },
  );
}

export async function completeIndustrialWorkflowCaseStage(
  caseId: string,
  payload: IndustrialWorkflowCaseCompleteStagePayload,
) {
  const response = await axiosInstance.post<IndustrialWorkflowCase>(
    `/industrial-workflows/cases/${caseId}/complete-stage`,
    payload,
  );

  return response.data;
}

export async function moveIndustrialWorkflowCaseStage(
  caseId: string,
  stageDefinitionId: string,
) {
  const response = await axiosInstance.post<IndustrialWorkflowCase>(
    `/industrial-workflows/cases/${caseId}/stage`,
    {
      stage_definition_id: stageDefinitionId,
    },
  );

  return response.data;
}

export async function getIndustrialWorkflowCaseStageSchedule(
  caseId: string,
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowCaseStageSchedule>(
      `/industrial-workflows/cases/${caseId}/schedule`,
    );

  return response.data;
}

export async function recalculateIndustrialWorkflowCaseStageSchedule(
  caseId: string,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowCaseStageScheduleRecalculationResult>(
      `/industrial-workflows/cases/${caseId}/schedule/recalculate`,
    );

  return response.data;
}

export async function previewIndustrialWorkflowCaseStageScheduleOverride(
  caseId: string,
  payload:
    IndustrialWorkflowCaseStageScheduleOverridePreviewPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowCaseStageScheduleOverridePreview>(
      `/industrial-workflows/cases/${caseId}/schedule/overrides/preview`,
      payload,
    );

  return response.data;
}

export async function applyIndustrialWorkflowCaseStageScheduleOverride(
  caseId: string,
  payload:
    IndustrialWorkflowCaseStageScheduleOverrideApplyPayload,
) {
  const response =
    await axiosInstance.post<IndustrialWorkflowCaseStageScheduleOverrideResult>(
      `/industrial-workflows/cases/${caseId}/schedule/overrides/apply`,
      payload,
    );

  return response.data;
}

export async function getIndustrialWorkflowOperationsDashboard(
  processDefinitionId: string,
  query = '',
) {
  const response =
    await axiosInstance.get<IndustrialWorkflowOperationsDashboard>(
      '/industrial-workflows/operations/dashboard',
      {
        params: {
          process_definition_id: processDefinitionId,
          q: query.trim() || undefined,
        },
      },
    );

  return response.data;
}

export async function getIndustrialWorkflowOperatorMyWork() {
  const response =
    await axiosInstance.get<IndustrialWorkflowOperatorMyWork>(
      '/industrial-workflows/operations/my-work',
    );

  return response.data;
}
